const pad = (value) => String(value).padStart(2, "0");
const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, date.getHours(), date.getMinutes());
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, date.getDate(), date.getHours(), date.getMinutes());
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const dayLabel = (date) => date.toLocaleDateString(document.documentElement.lang || "en", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const monthName = (month) => new Date(2026, month, 1).toLocaleString(document.documentElement.lang || "en", { month: "long" });
const parseDate = (value, format) => {
  // Parse the date and time using Flatpickr's date format.
  const date = window.flatpickr.parseDate(value, format);

  // Read the AM/PM value from the end of the original date string.
  const meridiem = String(value).trim().slice(-2).toUpperCase();

  // Leave dates without AM/PM unchanged, such as date-only or 24-hour values.
  if (meridiem !== "AM" && meridiem !== "PM") return date;

  // Use modulo to convert the parsed hour to a 12-hour value (13 becomes 1 and 12 becomes 0).
  const hour = date.getHours() % 12;

  // Convert the 12-hour value back to JavaScript's 24-hour format.
  date.setHours(hour + (meridiem === "PM" ? 12 : 0));

  // Return the corrected date.
  return date;
};
const formatDate = (date, format) => window.flatpickr?.formatDate(date, format) || date.toLocaleString();
const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
const hour12 = (date) => {
  const hour = date.getHours() % 12;
  return hour || 12;
};

class AccessibleCalendar {
  constructor(input, options = {}) {
    this.input = input instanceof HTMLElement ? input : document.querySelector(input);
    if (this.input._flatpickr instanceof AccessibleCalendar) return this.input._flatpickr;
    this.format = options.dateFormat || this.input.dataset.format || "Y-m-d h:i K";
    this.enableTime = options.enableTime ?? /[hHiK]/.test(this.format);
    this.selected = parseDate(options.defaultDate || this.input.dataset.default || this.input.value, this.format);
    this.focused = new Date(this.selected);
    this.min = this.input.dataset.min ? parseDate(this.input.dataset.min, this.format) : null;
    this.label = this.input.getAttribute("aria-label") || this.input.labels?.[0]?.textContent.trim() || "Choose date";
    this.root = this.input.closest(".accessible-calendar") || this.input.closest(".input-group") || this.input.parentElement;
    this.root.classList.add("accessible-calendar");
    this.button = this.root.querySelector(".accessible-calendar__button, .input-group-text.datetimepicker, .assignment-settings__datepicker-icon");
    this.appendTarget = options.appendTarget || this.input.closest(".modal") || document.body;
    this.closeOnOutsideClick = (event) => {
      if (!this.root.contains(event.target) && !this.calendar.contains(event.target)) this.close(false);
    };
    this.reposition = () => this.positionCalendar();
    this.build();
    this.bind();
    this.input.readOnly = true;
    this.input.setAttribute("aria-haspopup", "dialog");
    this.input.setAttribute("aria-expanded", "false");
    this.input.value = formatDate(this.selected, this.format);
    this.input._flatpickr = this;
  }

  build() {
    this.calendar = document.getElementById("accessible-calendar-template").content.firstElementChild.cloneNode(true);
    this.calendar.setAttribute("aria-label", `${this.label} calendar`);
    this.calendar.querySelectorAll("[data-calendar-id]").forEach((element) => {
      element.id = this.id(element.dataset.calendarId);
    });
    this.calendar.querySelectorAll("[data-calendar-for]").forEach((element) => {
      element.htmlFor = this.id(element.dataset.calendarFor);
    });
    if (!this.enableTime) this.calendar.querySelector(".accessible-calendar__time").remove();
    this.appendTarget.appendChild(this.calendar);
  }

  bind() {
    this.button?.addEventListener("click", () => this.open());
    this.input.addEventListener("click", () => this.open({ focusCalendar: false }));
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Tab" && !event.shiftKey && !this.calendar.hidden) {
        event.preventDefault();
        this.firstFocusable()?.focus();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        this.open();
      }
      if (event.altKey && event.key === "ArrowDown") this.open();
    });
    this.calendar.addEventListener("click", (event) => this.click(event));
    this.calendar.addEventListener("keydown", (event) => this.keydown(event));
    this.calendar.addEventListener("change", (event) => this.change(event));
    this.calendar.addEventListener("input", (event) => {
      if (event.target.matches(".accessible-calendar__hour, .accessible-calendar__minute")) this.setTime();
    });
  }

  open({ focusCalendar = true } = {}) {
    this.render();
    this.calendar.hidden = false;
    this.positionCalendar();
    this.input.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", this.closeOnOutsideClick);
    window.addEventListener("resize", this.reposition);
    window.addEventListener("scroll", this.reposition, true);
    if (focusCalendar) this.calendar.querySelector(`[data-date="${this.iso(this.focused)}"]`)?.focus();
  }

  close(restoreFocus = true) {
    this.calendar.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", this.closeOnOutsideClick);
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.reposition, true);
    if (restoreFocus) this.input.focus();
  }

  setDate(date, triggerChange = false) {
    this.selected = date instanceof Date ? date : parseDate(date, this.format);
    this.focused = new Date(this.selected);
    this.input.value = formatDate(this.selected, this.format);
    if (triggerChange) this.input.dispatchEvent(new Event("change", { bubbles: true }));
    this.render();
  }

  set(name, value) {
    if (name === "minDate") this.min = value instanceof Date ? value : parseDate(value, this.format);
    this.render();
  }

  positionCalendar() {
    if (this.calendar.hidden) return;

    const gap = 4;
    const margin = 8;
    const anchor = this.root.getBoundingClientRect();
    const popup = this.calendar.getBoundingClientRect();
    const maxLeft = window.innerWidth - popup.width - margin;
    const left = clamp(anchor.left, margin, maxLeft);
    const top = popup.height + gap > window.innerHeight - anchor.bottom && anchor.top > window.innerHeight - anchor.bottom
      ? anchor.top - popup.height - gap
      : anchor.bottom + gap;

    this.calendar.style.left = `${left}px`;
    this.calendar.style.top = `${clamp(top, margin, window.innerHeight - popup.height - margin)}px`;
  }

  click(event) {
    const day = event.target.closest("[data-date]");
    if (day) this.choose(this.dateFromIso(day.dataset.date));
    if (event.target.matches(".accessible-calendar__nav")) this.moveMonth(Number(event.target.dataset.move), event.target);
    if (event.target.matches(".accessible-calendar__today")) this.choose(this.todayWithSelectedTime());
  }

  change(event) {
    if (event.target.matches(".accessible-calendar__month, .accessible-calendar__year")) {
      this.focused = new Date(
        this.numberValue(".accessible-calendar__year", this.focused.getFullYear()),
        Number(this.calendar.querySelector(".accessible-calendar__month").value),
        Math.min(this.focused.getDate(), 28),
        this.selected.getHours(),
        this.selected.getMinutes()
      );
      this.render();
    }
    if (this.enableTime && event.target.matches(".accessible-calendar__hour, .accessible-calendar__minute, .accessible-calendar__meridiem")) {
      this.setTime();
      if (event.target.matches(".accessible-calendar__minute")) event.target.value = pad(this.selected.getMinutes());
    }
  }

  keydown(event) {
    if (event.key === "Tab") {
      this.trapTab(event);
      return;
    }

    if (event.target.matches(".accessible-calendar__select, .accessible-calendar__number")) {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        event.target.showPicker?.();
      }
      if (event.target.matches(".accessible-calendar__select") && event.key === " ") {
        event.preventDefault();
        event.target.showPicker?.();
      }
      if (event.key === "Escape") this.close();
      return;
    }

    if (!event.target.matches(".accessible-calendar__day")) return;

    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (moves[event.key]) this.move(addDays(this.focused, moves[event.key]), event);
    if (event.key === "PageUp") this.move(addMonths(this.focused, event.shiftKey ? -12 : -1), event);
    if (event.key === "PageDown") this.move(addMonths(this.focused, event.shiftKey ? 12 : 1), event);
    if (event.key === "Home") this.move(addDays(this.focused, -this.focused.getDay()), event);
    if (event.key === "End") this.move(addDays(this.focused, 6 - this.focused.getDay()), event);
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
      this.choose(this.dateFromIso(event.target.dataset.date), event);
    }
    if (event.key === "Escape") {
      event.stopPropagation();
      this.close();
    }
  }

  move(date, event) {
    event?.preventDefault();
    if (this.disabled(date)) return;
    this.focused = date;
    this.render();
    this.calendar.querySelector(`[data-date="${this.iso(date)}"]`)?.focus();
  }

  moveMonth(months, control) {
    this.focused = addMonths(this.focused, months);
    this.render();
    control.focus();
  }

  choose(date, event) {
    event?.preventDefault();
    if (this.disabled(date)) return;
    if (this.enableTime) this.applyTime(date);
    this.setDate(date, true);
    this.close();
  }

  setTime() {
    if (!this.enableTime) return;
    this.applyTime(this.selected);
    this.input.value = formatDate(this.selected, this.format);
  }

  applyTime(date) {
    const hour = Math.max(1, Math.min(12, this.numberValue(".accessible-calendar__hour", hour12(this.selected))));
    const minute = Math.max(0, Math.min(59, this.numberValue(".accessible-calendar__minute", this.selected.getMinutes())));
    const meridiem = this.calendar.querySelector(".accessible-calendar__meridiem").value;
    date.setHours((hour % 12) + (meridiem === "PM" ? 12 : 0), minute);
  }

  render() {
    const first = new Date(this.focused.getFullYear(), this.focused.getMonth(), 1);
    const start = addDays(first, -first.getDay());
    this.calendar.querySelector(".accessible-calendar__month").innerHTML = this.monthOptions();
    this.calendar.querySelector(".accessible-calendar__year").value = this.focused.getFullYear();
    if (this.enableTime) {
      this.calendar.querySelector(".accessible-calendar__hour").value = hour12(this.selected);
      this.calendar.querySelector(".accessible-calendar__minute").value = pad(this.selected.getMinutes());
      this.calendar.querySelector(".accessible-calendar__meridiem").value = this.selected.getHours() >= 12 ? "PM" : "AM";
    }
    this.calendar.querySelector(".accessible-calendar__grid").innerHTML = Array.from({ length: 42 }, (_day, index) => this.dayButton(addDays(start, index))).join("");
    this.positionCalendar();
  }

  monthOptions() {
    return Array.from({ length: 12 }, (_month, index) => `<option value="${index}" ${index === this.focused.getMonth() ? "selected" : ""}>${monthName(index)}</option>`).join("");
  }

  dayButton(date) {
    const disabled = this.disabled(date);
    const selected = sameDay(date, this.selected);
    const today = sameDay(date, new Date());
    const todayStart = startOfDay(new Date());
    const dateStart = startOfDay(date);
    const inMonth = date.getMonth() === this.focused.getMonth();
    const past = dateStart < todayStart;
    const future = dateStart > todayStart;
    const classes = [
      "accessible-calendar__day",
      future && !inMonth && !disabled ? "accessible-calendar__day--future" : "",
      past ? "accessible-calendar__day--past" : "",
      disabled ? "accessible-calendar__day--disabled" : ""
    ].filter(Boolean).join(" ");
    return `<button type="button"
      class="${classes}"
      data-date="${this.iso(date)}"
      aria-label="${dayLabel(date)}"
      aria-selected="${selected}"
      ${today ? "aria-current=\"date\"" : ""}
      ${disabled ? "disabled aria-disabled=\"true\"" : ""}>${date.getDate()}</button>`;
  }

  disabled(date) {
    return this.min && date < new Date(this.min.getFullYear(), this.min.getMonth(), this.min.getDate());
  }

  todayWithSelectedTime() {
    const today = new Date();
    if (this.enableTime) today.setHours(this.selected.getHours(), this.selected.getMinutes());
    return today;
  }

  dateFromIso(value) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, this.enableTime ? this.selected.getHours() : 0, this.enableTime ? this.selected.getMinutes() : 0);
  }

  iso(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  id(suffix) {
    return `${this.input.id || this.input.name || "accessible-calendar"}-${suffix}`;
  }

  numberValue(selector, fallback) {
    const value = Number(this.calendar.querySelector(selector).value);
    return Number.isNaN(value) ? fallback : value;
  }

  focusableControls() {
    return Array.from(this.calendar.querySelectorAll("button:not([disabled]), select:not([disabled]), input:not([disabled])"))
      .filter((element) => element.offsetParent !== null);
  }

  firstFocusable() {
    return this.focusableControls()[0];
  }

  trapTab(event) {
    const controls = this.focusableControls();
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

window.createAccessibleCalendar = (input, options = {}) => new AccessibleCalendar(input, options);
